import { Alert, Box, Tab, Tabs } from '@mui/material';

import { SERVER_ROWS_PER_PAGE_OPTIONS } from '../../common/constants/paginationConstants';
import {
  CustomButton,
  FormTitleBox,
  InfoTooltip,
  PaginationTable,
} from '../../common/components';
import MaterialOpeningDetailDrawer from './components/MaterialOpeningDetailDrawer';
import MaterialOpeningDrawer from './components/MaterialOpeningDrawer';
import MaterialPreflightTable from './components/MaterialPreflightTable';
import MaterialValuationDataTable from './components/MaterialValuationDataTable';
import MaterialValuationFilters from './components/MaterialValuationFilters';
import {
  VALUATION_TAB_LABELS,
  VALUATION_TABS,
  VALUATION_TEXTS,
} from './constants/materialValuationConstants';
import { useMaterialValuation } from './hooks/useMaterialValuation';

const tabsSx = {
  minHeight: 44,
  '& .MuiTab-root': {
    minHeight: 44,
    fontSize: 13,
    fontWeight: 700,
  },
};

const PURCHASE_COLUMNS = [
  { key: 'article', label: VALUATION_TEXTS.article },
  { key: 'pending_quantity', label: VALUATION_TEXTS.pendingQuantity, numeric: true, width: 200 },
  {
    key: 'documented_price_ars',
    label: VALUATION_TEXTS.documentedPrice,
    numeric: true,
    width: 240,
  },
];

const RETURN_COLUMNS = [
  { key: 'article_name', label: VALUATION_TEXTS.article },
  { key: 'quantity', label: VALUATION_TEXTS.quantity, numeric: true, width: 200 },
];

const MaterialValuation = () => {
  const {
    activeTab,
    canReview,
    canSubmit,
    changeField,
    changeStockFilter,
    changeTab,
    clearStockFilters,
    closeDetail,
    closeDrawer,
    detailError,
    detailOpen,
    drawerOpen,
    error,
    form,
    handlePageChange,
    handleRowsPerPageChange,
    rowsPerPage,
    isDetailLoading,
    isLoading,
    isSubmitting,
    loadError,
    openDetail,
    openReview,
    opening,
    page,
    preview,
    refetch,
    rows,
    sortStocks,
    sources,
    stockFilters,
    stockSort,
    submit,
    totalCount,
    warehouses,
  } = useMaterialValuation();

  const isPurchasesTab = activeTab === VALUATION_TABS.purchases;

  return (
    <Box>
      <FormTitleBox title={VALUATION_TEXTS.title}>
        <InfoTooltip lines={[VALUATION_TEXTS.info, VALUATION_TEXTS.legacyNote]} />
      </FormTitleBox>

      {loadError && (
        <Alert
          severity="error"
          action={(
            <CustomButton variant="outlined" size="small" onClick={() => refetch()}>
              {VALUATION_TEXTS.retry}
            </CustomButton>
          )}
        >
          {loadError}
        </Alert>
      )}

      <Box sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={changeTab}
          variant="scrollable"
          scrollButtons="auto"
          sx={tabsSx}
        >
          <Tab value={VALUATION_TABS.stocks} label={VALUATION_TAB_LABELS.stocks} />
          <Tab
            value={VALUATION_TABS.purchases}
            label={VALUATION_TAB_LABELS.purchases(sources.purchaseCount)}
          />
          <Tab
            value={VALUATION_TABS.returns}
            label={VALUATION_TAB_LABELS.returns(sources.returnCount)}
          />
        </Tabs>
      </Box>

      {activeTab === VALUATION_TABS.stocks ? (
        <>
          <MaterialValuationFilters
            variant="stocks"
            filters={stockFilters}
            warehouses={warehouses}
            onChange={changeStockFilter}
            onClear={clearStockFilters}
          />
          <MaterialValuationDataTable
            rows={rows}
            isLoading={isLoading}
            canReview={canReview}
            onReview={openReview}
            onViewOpening={openDetail}
            sort={stockSort}
            onSort={sortStocks}
          />
          {totalCount > 0 && (
            <PaginationTable
              page={page}
              totalPages={Math.max(1, Math.ceil(totalCount / rowsPerPage))}
              totalCount={totalCount}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={SERVER_ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={handleRowsPerPageChange}
              onPageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        <>
          <MaterialValuationFilters
            variant="sources"
            filters={{ search: sources.search }}
            onChange={(_, value) => sources.changeSearch(value)}
            onClear={sources.reset}
          />
          <MaterialPreflightTable
            rows={sources.rows}
            columns={isPurchasesTab ? PURCHASE_COLUMNS : RETURN_COLUMNS}
            totalCount={sources.totalCount}
            page={sources.page}
            rowsPerPage={sources.rowsPerPage}
            onRowsPerPageChange={sources.handleRowsPerPageChange}
            onPageChange={sources.changePage}
            isLoading={sources.isLoading}
            emptyMessage={
              isPurchasesTab ? VALUATION_TEXTS.emptyPurchases : VALUATION_TEXTS.emptyReturns
            }
            sort={sources.sort}
            onSort={sources.toggleSort}
          />
        </>
      )}

      <MaterialOpeningDetailDrawer
        open={detailOpen}
        opening={opening}
        isLoading={isDetailLoading}
        error={detailError}
        onClose={closeDetail}
      />

      <MaterialOpeningDrawer
        open={drawerOpen}
        form={form}
        preview={preview}
        error={error}
        isSubmitting={isSubmitting}
        canSubmit={canSubmit}
        onClose={closeDrawer}
        onChange={changeField}
        onSubmit={submit}
      />
    </Box>
  );
};

export default MaterialValuation;
